import {
  Box,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";

interface TablePaginationBarProps {
  /** Total rows across all pages. */
  count: number;
  /** Zero-based page index, matching MUI's TablePagination convention. */
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

/**
 * Footer strip below a data table: range summary on the left,
 * numbered pager on the right.
 */
export const TablePaginationBar = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [25, 50, 100],
}: TablePaginationBarProps) => {
  const pageCount = Math.max(Math.ceil(count / rowsPerPage), 1);
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
        px: 2,
        py: 1.5,
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Zeige {from}–{to} von {count} Einträgen
        </Typography>

        {onRowsPerPageChange && (
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(event) => onRowsPerPageChange(Number(event.target.value))}
            sx={{ "& .MuiSelect-select": { py: 0.5, fontSize: "0.8125rem" } }}
            aria-label="Einträge pro Seite"
          >
            {rowsPerPageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option} / Seite
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>

      <Pagination
        // MUI's Pagination is 1-based; the caller works in 0-based pages.
        count={pageCount}
        page={page + 1}
        onChange={(_, nextPage) => onPageChange(nextPage - 1)}
        shape="rounded"
        siblingCount={1}
        boundaryCount={1}
      />
    </Box>
  );
};

export default TablePaginationBar;
